<template>
  <div class="score-template-manage">
    <a-page-header title="评分标准管理" sub-title="管理测试用例的评分模板">
      <template #extra>
        <a-button type="primary" @click="showCreateDialog">
          <template #icon><PlusOutlined /></template>
          创建评分模板
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <!-- 筛选区域 -->
      <a-form layout="inline" style="margin-bottom: 16px">
        <a-form-item label="评估目标">
          <a-select 
            v-model:value="filters.eval_target" 
            style="width: 150px" 
            allow-clear 
            placeholder="全部"
            @change="handleFilterChange"
          >
            <a-select-option value="">全部</a-select-option>
            <a-select-option v-for="t in evalTargets" :key="t.value" :value="t.value">
              {{ t.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="状态">
          <a-select 
            v-model:value="filters.is_active" 
            style="width: 100px"
            @change="handleFilterChange"
          >
            <a-select-option :value="undefined">全部</a-select-option>
            <a-select-option :value="true">启用</a-select-option>
            <a-select-option :value="false">禁用</a-select-option>
          </a-select>
        </a-form-item>
      </a-form>

      <!-- 表格 -->
      <a-table
        :columns="columns"
        :data-source="templates"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'template_name'">
            <span>{{ record.template_name }}</span>
            <a-tag v-if="record.is_default" color="blue" style="margin-left: 8px">默认</a-tag>
          </template>
          <template v-else-if="column.key === 'eval_target'">
            <a-tag v-if="record.eval_target" :color="getTargetColor(record.eval_target)">
              {{ record.eval_target_label || record.eval_target }}
            </a-tag>
            <span v-else>-</span>
          </template>
          <template v-else-if="column.key === 'pass_threshold'">
            <a-tag :color="getThresholdColor(record.pass_threshold)">
              {{ record.pass_threshold }}级及以上
            </a-tag>
          </template>
          <template v-else-if="column.key === 'is_active'">
            <a-switch 
              :checked="record.is_active" 
              @change="(checked) => handleActiveChange(record.id, checked)"
            />
          </template>
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="viewTemplate(record)">查看</a-button>
              <a-button type="link" size="small" @click="editTemplate(record)">编辑</a-button>
              <a-button 
                type="link" 
                size="small" 
                @click="setAsDefault(record.id)"
                v-if="!record.is_default"
              >
                设为默认
              </a-button>
              <a-popconfirm
                title="确定要删除此评分模板吗？"
                @confirm="deleteTemplate(record.id)"
                v-if="!record.is_default"
              >
                <a-button type="link" size="small" danger>删除</a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 创建/编辑对话框 -->
    <a-modal
      v-model:open="dialogVisible"
      :title="editingTemplate ? '编辑评分模板' : '创建评分模板'"
      width="700px"
      @ok="handleSave"
      @cancel="handleCancel"
    >
      <a-form
        ref="formRef"
        :model="formData"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
      >
        <a-form-item label="模板名称" required>
          <a-input v-model:value="formData.template_name" placeholder="请输入模板名称" />
        </a-form-item>
        <a-form-item label="模板编码">
          <a-input v-model:value="formData.template_code" placeholder="留空自动生成" />
        </a-form-item>
        <a-form-item label="评估目标">
          <a-select v-model:value="formData.eval_target" placeholder="请选择评估目标" allow-clear>
            <a-select-option v-for="t in evalTargets" :key="t.value" :value="t.value">
              {{ t.label }} - {{ t.description }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea v-model:value="formData.description" :rows="2" />
        </a-form-item>
        
        <a-divider>评分标准</a-divider>
        
        <a-form-item label="A级标准（优秀）" required>
          <a-textarea
            v-model:value="formData.criteria_a"
            placeholder="完全符合预期，准确率>95%"
            :rows="2"
          />
        </a-form-item>
        <a-form-item label="B级标准（良好）" required>
          <a-textarea
            v-model:value="formData.criteria_b"
            placeholder="基本符合预期，准确率>80%"
            :rows="2"
          />
        </a-form-item>
        <a-form-item label="C级标准（及格）" required>
          <a-textarea
            v-model:value="formData.criteria_c"
            placeholder="部分符合预期，准确率>60%"
            :rows="2"
          />
        </a-form-item>
        <a-form-item label="通过阈值">
          <a-select v-model:value="formData.pass_threshold">
            <a-select-option value="A">A级及以上</a-select-option>
            <a-select-option value="B">B级及以上</a-select-option>
            <a-select-option value="C">C级及以上</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="输出维度">
          <a-select
            v-model:value="formData.output_dimensions"
            mode="tags"
            placeholder="定义评估报告的输出维度"
            style="width: 100%"
          >
            <a-select-option value="一致性等级">一致性等级</a-select-option>
            <a-select-option value="稳定性得分">稳定性得分</a-select-option>
            <a-select-option value="差异分析">差异分析</a-select-option>
            <a-select-option value="改进建议">改进建议</a-select-option>
            <a-select-option value="模型排名">模型排名</a-select-option>
            <a-select-option value="各模型得分">各模型得分</a-select-option>
            <a-select-option value="优劣对比">优劣对比</a-select-option>
            <a-select-option value="推荐模型">推荐模型</a-select-option>
            <a-select-option value="最优结构">最优结构</a-select-option>
            <a-select-option value="结构影响分析">结构影响分析</a-select-option>
            <a-select-option value="优化建议">优化建议</a-select-option>
            <a-select-option value="知识覆盖度影响">知识覆盖度影响</a-select-option>
            <a-select-option value="关键知识识别">关键知识识别</a-select-option>
            <a-select-option value="知识推荐">知识推荐</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="适用分类">
          <a-select
            v-model:value="formData.applicable_categories"
            mode="tags"
            placeholder="留空表示适用于所有分类"
            style="width: 100%"
          >
            <a-select-option value="故障诊断">故障诊断</a-select-option>
            <a-select-option value="知识查询">知识查询</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="设为默认" v-if="!editingTemplate?.is_default">
          <a-switch v-model:checked="formData.is_default" />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 查看详情对话框 -->
    <a-modal
      v-model:open="viewDialogVisible"
      title="评分模板详情"
      width="600px"
      :footer="null"
    >
      <a-descriptions :column="1" bordered v-if="viewingTemplate">
        <a-descriptions-item label="模板名称">
          {{ viewingTemplate.template_name }}
          <a-tag v-if="viewingTemplate.is_default" color="blue" style="margin-left: 8px">默认</a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="模板编码">{{ viewingTemplate.template_code }}</a-descriptions-item>
        <a-descriptions-item label="评估目标">
          <a-tag v-if="viewingTemplate.eval_target" :color="getTargetColor(viewingTemplate.eval_target)">
            {{ viewingTemplate.eval_target_label || viewingTemplate.eval_target }}
          </a-tag>
          <span v-else>通用模板</span>
        </a-descriptions-item>
        <a-descriptions-item label="描述">{{ viewingTemplate.description || '-' }}</a-descriptions-item>
        <a-descriptions-item label="A级标准（优秀）">
          <div class="criteria-text">{{ viewingTemplate.criteria_a }}</div>
        </a-descriptions-item>
        <a-descriptions-item label="B级标准（良好）">
          <div class="criteria-text">{{ viewingTemplate.criteria_b }}</div>
        </a-descriptions-item>
        <a-descriptions-item label="C级标准（及格）">
          <div class="criteria-text">{{ viewingTemplate.criteria_c }}</div>
        </a-descriptions-item>
        <a-descriptions-item label="通过阈值">
          <a-tag :color="getThresholdColor(viewingTemplate.pass_threshold)">
            {{ viewingTemplate.pass_threshold }}级及以上
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="输出维度">
          <template v-if="viewingTemplate.output_dimensions?.length">
            <a-tag v-for="dim in viewingTemplate.output_dimensions" :key="dim" color="purple">{{ dim }}</a-tag>
          </template>
          <span v-else>-</span>
        </a-descriptions-item>
        <a-descriptions-item label="适用分类">
          <template v-if="viewingTemplate.applicable_categories?.length">
            <a-tag v-for="cat in viewingTemplate.applicable_categories" :key="cat">{{ cat }}</a-tag>
          </template>
          <span v-else>全部分类</span>
        </a-descriptions-item>
        <a-descriptions-item label="创建时间">{{ formatTime(viewingTemplate.create_time) }}</a-descriptions-item>
      </a-descriptions>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { PlusOutlined } from '@ant-design/icons-vue';
import {
  getScoreTemplates,
  createScoreTemplate,
  updateScoreTemplate,
  deleteScoreTemplate as deleteTemplateAPI,
  getEvalTargets
} from '@/api/evalScoreTemplate';

// 数据
const templates = ref([]);
const evalTargets = ref([]);
const loading = ref(false);

// 筛选
const filters = reactive({
  eval_target: undefined,
  is_active: undefined
});

// 分页
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showTotal: (total) => `共 ${total} 条`
});

// 对话框
const dialogVisible = ref(false);
const viewDialogVisible = ref(false);
const editingTemplate = ref(null);
const viewingTemplate = ref(null);

// 表单
const formData = reactive({
  template_name: '',
  template_code: '',
  eval_target: undefined,
  description: '',
  criteria_a: '',
  criteria_b: '',
  criteria_c: '',
  pass_threshold: 'B',
  output_dimensions: [],
  applicable_categories: [],
  is_default: false
});

// 表格列
const columns = [
  { title: '模板名称', dataIndex: 'template_name', key: 'template_name', width: 200 },
  { title: '模板编码', dataIndex: 'template_code', key: 'template_code', width: 120 },
  { title: '评估目标', dataIndex: 'eval_target', key: 'eval_target', width: 100 },
  { title: '描述', dataIndex: 'description', key: 'description', ellipsis: true },
  { title: '通过阈值', dataIndex: 'pass_threshold', key: 'pass_threshold', width: 100 },
  { title: '启用', dataIndex: 'is_active', key: 'is_active', width: 80 },
  { title: '操作', key: 'action', width: 240, fixed: 'right' }
];

// 方法
const fetchEvalTargets = async () => {
  try {
    const res = await getEvalTargets();
    if (res.code === 200 && res.data) {
      evalTargets.value = res.data;
    }
  } catch (error) {
    console.error('获取评估目标失败：', error);
  }
};

const fetchTemplates = async () => {
  loading.value = true;
  try {
    const params = {
      page: pagination.current,
      pageSize: pagination.pageSize
    };
    if (filters.eval_target) {
      params.eval_target = filters.eval_target;
    }
    if (filters.is_active !== undefined) {
      params.is_active = filters.is_active;
    }
    const res = await getScoreTemplates(params);
    if (res.code === 200 && res.data) {
      templates.value = res.data.list || [];
      pagination.total = res.data.pagination?.total || 0;
    }
  } catch (error) {
    message.error('获取评分模板失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

const handleFilterChange = () => {
  pagination.current = 1;
  fetchTemplates();
};

const handleTableChange = (pag) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  fetchTemplates();
};

const showCreateDialog = () => {
  editingTemplate.value = null;
  resetForm();
  dialogVisible.value = true;
};

const editTemplate = (record) => {
  editingTemplate.value = record;
  Object.assign(formData, {
    template_name: record.template_name,
    template_code: record.template_code,
    eval_target: record.eval_target || undefined,
    description: record.description,
    criteria_a: record.criteria_a,
    criteria_b: record.criteria_b,
    criteria_c: record.criteria_c,
    pass_threshold: record.pass_threshold,
    output_dimensions: record.output_dimensions || [],
    applicable_categories: record.applicable_categories || [],
    is_default: record.is_default
  });
  dialogVisible.value = true;
};

const viewTemplate = (record) => {
  viewingTemplate.value = record;
  viewDialogVisible.value = true;
};

const handleSave = async () => {
  try {
    if (!formData.template_name || !formData.criteria_a || !formData.criteria_b || !formData.criteria_c) {
      message.error('请填写必填项');
      return;
    }

    const data = {
      template_name: formData.template_name,
      template_code: formData.template_code || undefined,
      eval_target: formData.eval_target || null,
      description: formData.description,
      criteria_a: formData.criteria_a,
      criteria_b: formData.criteria_b,
      criteria_c: formData.criteria_c,
      pass_threshold: formData.pass_threshold,
      output_dimensions: formData.output_dimensions?.length ? formData.output_dimensions : null,
      applicable_categories: formData.applicable_categories?.length ? formData.applicable_categories : null,
      is_default: formData.is_default,
      created_by: 'admin'
    };

    if (editingTemplate.value) {
      await updateScoreTemplate(editingTemplate.value.id, data);
      message.success('更新成功');
    } else {
      await createScoreTemplate(data);
      message.success('创建成功');
    }
    
    dialogVisible.value = false;
    fetchTemplates();
  } catch (error) {
    message.error('保存失败：' + error.message);
  }
};

const handleCancel = () => {
  dialogVisible.value = false;
  resetForm();
};

const resetForm = () => {
  Object.assign(formData, {
    template_name: '',
    template_code: '',
    eval_target: undefined,
    description: '',
    criteria_a: '',
    criteria_b: '',
    criteria_c: '',
    pass_threshold: 'B',
    output_dimensions: [],
    applicable_categories: [],
    is_default: false
  });
};

const setAsDefault = async (id) => {
  try {
    await updateScoreTemplate(id, { is_default: true });
    message.success('已设为默认模板');
    fetchTemplates();
  } catch (error) {
    message.error('设置失败：' + error.message);
  }
};

const handleActiveChange = async (id, checked) => {
  try {
    await updateScoreTemplate(id, { is_active: checked });
    message.success(checked ? '已启用' : '已禁用');
    fetchTemplates();
  } catch (error) {
    message.error('操作失败：' + error.message);
  }
};

const deleteTemplate = async (id) => {
  try {
    await deleteTemplateAPI(id);
    message.success('删除成功');
    fetchTemplates();
  } catch (error) {
    message.error('删除失败：' + error.message);
  }
};

const getThresholdColor = (threshold) => {
  const colors = {
    A: 'green',
    B: 'blue',
    C: 'orange'
  };
  return colors[threshold] || 'default';
};

const getTargetColor = (target) => {
  const colors = {
    consistency: 'cyan',
    model_comparison: 'purple',
    knowledge_structure: 'orange',
    knowledge_content: 'green'
  };
  return colors[target] || 'default';
};

const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  const time = new Date(timeStr);
  return time.toLocaleString('zh-CN');
};

// 初始化
onMounted(() => {
  fetchEvalTargets();
  fetchTemplates();
});
</script>

<style scoped>
.score-template-manage {
  padding: 24px;
}

.criteria-text {
  white-space: pre-wrap;
  line-height: 1.6;
}
</style>

